import { useEffect, useMemo, useState } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import PropertyHero from './components/PropertyHero';
import { GalleryGrid, GalleryLightbox } from './components/PropertyGallery';
import PropertyMatterport from './components/PropertyMatterport';
import PropertyStory from './components/PropertyStory';
import SignatureFeatures from './components/SignatureFeatures';
import EssentialInfo from './components/EssentialInfo';
import PropertyConcierge from './components/PropertyConcierge';
import PropertyReviews from './components/PropertyReviews';
import StickyBookBar from './components/StickyBookBar';
import OwnerRezWidgets from './components/OwnerRezWidgets';
import { ownerRezWidgetsForProperty } from '@/mocks/ownerRezWidgets';
import { conciergeConfig, reviews } from './data/belvedere';
import type {
  EssentialInfoGroup,
  GalleryImage,
  PropertyHeroData,
  PropertyStoryData,
  SignatureFeature,
} from './types';

type OwnerRezSnapshot = {
  generated_at: string;
  custom_fields?: {
    matterport_url?: string;
    youtube_or_vimeo_url?: string;
  };
  property: {
    name: string;
    city: string;
    state: string;
    street1: string;
    postal_code: string;
    bedrooms: number;
    bathrooms: number;
    bathrooms_full: number;
    bathrooms_half: number;
    max_guests: number;
    living_area?: number;
    living_area_type?: string;
    thumbnail_url_large?: string;
  };
  listing: {
    headline?: string;
    description?: string;
    photos: { src: string; caption?: string }[];
    categories: Record<string, string[]>;
  };
};

const snapshotUrl = '/ownerrez-poc/belvedere.json';
const elevatorImage = '/images/properties/belvedere-elevator1a-web.png';

function cleanText(value = '') {
  return value
    .replace(/&ndash;/g, '-')
    .replace(/&amp;/g, '&')
    .replace(/\r/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function sectionFromDescription(description: string, heading: string) {
  const normalized = `\n${description}`;
  const start = normalized.indexOf(`\n${heading}\n`);
  if (start === -1) return '';

  const afterHeading = normalized.slice(start + heading.length + 2);
  const nextHeading = afterHeading.search(/\n(?:Level \d|Location|Property Description|The Belvedere: Property Features)\n/);
  return cleanText(nextHeading === -1 ? afterHeading : afterHeading.slice(0, nextHeading));
}

function firstSentences(text: string, count: number) {
  return cleanText(text)
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean)
    .slice(0, count)
    .join(' ');
}

function contains(items: string[], pattern: RegExp) {
  return items.find((item) => pattern.test(item));
}

function category(snapshot: OwnerRezSnapshot, key: string) {
  return (snapshot.listing.categories[key] ?? []).map(cleanText).filter(Boolean);
}

function makeGallery(snapshot: OwnerRezSnapshot): GalleryImage[] {
  const photos = snapshot.listing.photos;
  const featuredOrder = [0, 1, 2, 3, 4, 16, 5, 6, 7, 8, 9, 10];
  const orderedPhotos = [
    ...featuredOrder.map((index) => photos[index]).filter(Boolean),
    ...photos.filter((_, index) => !featuredOrder.includes(index)),
  ];

  return orderedPhotos.map((photo, index) => ({
    src: photo.src,
    alt: photo.caption || `${snapshot.property.name} photo ${index + 1}`,
    caption: photo.caption || `Photo ${index + 1} of ${orderedPhotos.length}`,
  }));
}

function sourcePhoto(snapshot: OwnerRezSnapshot, index: number) {
  return snapshot.listing.photos[index]?.src ?? snapshot.listing.photos[0]?.src ?? '';
}

function makeHero(snapshot: OwnerRezSnapshot, gallery: GalleryImage[]): PropertyHeroData {
  const mediaFields = snapshot.custom_fields ?? {};

  return {
    id: 'belvedere',
    name: snapshot.property.name,
    location: snapshot.property.city,
    area: '30A',
    bedrooms: String(snapshot.property.bedrooms),
    bathrooms: String(snapshot.property.bathrooms),
    sleeps: `Up to ${snapshot.property.max_guests}`,
    heroImage: gallery[0]?.src ?? snapshot.property.thumbnail_url_large ?? '',
    videoUrl: mediaFields.youtube_or_vimeo_url,
    matterportUrl: mediaFields.matterport_url,
  };
}

function makeEssentialGroups(snapshot: OwnerRezSnapshot): EssentialInfoGroup[] {
  const { property } = snapshot;
  const bedrooms = category(snapshot, 'Bedrooms');
  const bathrooms = category(snapshot, 'Bathrooms');
  const kitchen = category(snapshot, 'KitchenFeatures');
  const outdoor = category(snapshot, 'OutdoorFeatures');
  const popular = category(snapshot, 'PopularAmenityFeatures');
  const parking = category(snapshot, 'ParkingFeatures');
  const family = category(snapshot, 'FamilyFeatures');
  const services = category(snapshot, 'OtherServiceFeatures');
  const sport = category(snapshot, 'SportFeatures');
  const leisure = category(snapshot, 'LeisureFeatures');

  return [
    {
      heading: 'At a glance',
      items: [
        `${property.bedrooms} bedrooms`,
        `${property.bathrooms} bathrooms (${property.bathrooms_full} full, ${property.bathrooms_half} half)`,
        `Sleeps up to ${property.max_guests} guests`,
        property.living_area ? `${property.living_area.toLocaleString()} ${property.living_area_type} over 5 levels` : '',
        'Located in the heart of Rosemary Beach',
        contains(parking, /garage/i) ? 'Private garage and additional parking options' : '',
      ].filter(Boolean),
    },
    {
      heading: 'Key features',
      items: [
        'Private elevator with access to all 5 levels',
        'Private pool on Level 2',
        'Rooftop outdoor living area with Gulf and Rosemary Beach views',
        'Primary suite with two private bathrooms, sitting room, and balcony',
        contains(outdoor, /grill/i) ? 'Outdoor grill for relaxed evenings at the house' : '',
      ].filter(Boolean),
    },
    {
      heading: 'The kitchen',
      items: [
        contains(kitchen, /Kitchen Island/i) ? 'Large kitchen island made for gathering' : '',
        contains(kitchen, /Refrigerator/i) ? 'Full kitchen with refrigerator, stove, oven, and microwave' : '',
        contains(kitchen, /Dishwasher/i) ? 'Dishwasher and full dishware setup' : '',
        contains(kitchen, /Coffee Maker/i) ? 'Coffee maker, toaster, cookware, and dining essentials' : '',
        contains(kitchen, /Dining Table/i) ? 'Dining table for meals together' : '',
      ].filter(Boolean),
    },
    {
      heading: 'The suites',
      items: [
        ...bedrooms.slice(1, 6),
        'Two custom bunk rooms for kids or larger groups',
      ],
    },
    {
      heading: 'Comforts included',
      items: [
        contains(popular, /Linens/i) ? 'Linens provided' : '',
        contains(popular, /Towels/i) ? 'Towels provided' : '',
        contains(popular, /Wifi|Internet/i) ? 'Internet / Wi-Fi' : '',
        contains(popular, /Washing Machine/i) && contains(popular, /Clothes Dryer/i) ? 'Washer and dryer' : '',
        contains(popular, /Air Conditioning/i) ? 'Air conditioning and central heating' : '',
      ].filter(Boolean),
    },
    {
      heading: 'Family & service',
      items: [
        ...family,
        ...services,
      ],
    },
    {
      heading: 'Bathrooms',
      items: bathrooms.slice(0, 7),
    },
    {
      heading: 'Nearby experiences',
      items: [
        ...sport.slice(0, 5),
        ...leisure.slice(0, 4),
      ],
    },
  ].filter((group) => group.items.length > 0);
}

function makeStory(snapshot: OwnerRezSnapshot): PropertyStoryData {
  const description = snapshot.listing.description ?? '';
  const level1 = sectionFromDescription(description, 'Level 1');
  const level2 = sectionFromDescription(description, 'Level 2');
  const level3 = sectionFromDescription(description, 'Level 3');
  const level4 = sectionFromDescription(description, 'Level 4');
  const level5 = sectionFromDescription(description, 'Level 5');
  const location = sectionFromDescription(description, 'Location');

  return {
    heading: 'Rosemary Beach luxury,',
    subheading: 'made warm, useful, and easy to choose.',
    paragraphs: [
      "The Belvedere brings together five levels, 7 bedrooms, 9 bathrooms, a private pool, private elevator, rooftop living, and room for up to 20 guests in the heart of Rosemary Beach.",
      level1 ? firstSentences(level1, 3) : '',
      level2 ? firstSentences(level2, 5) : '',
      [level3, level4].filter(Boolean).map((section) => firstSentences(section, 2)).join(' '),
      level5 ? firstSentences(level5, 3) : '',
    ].filter(Boolean),
    locationContext:
      location ||
      `${snapshot.property.name} is located in the heart of ${snapshot.property.city}, close to dining, shopping, and 30A beach life.`,
    address: snapshot.property.street1,
    addressSub: `${snapshot.property.city}, ${snapshot.property.state} ${snapshot.property.postal_code}`,
    propertyMeta: `${snapshot.property.bedrooms} bedrooms - ${snapshot.property.bathrooms} bathrooms - sleeps up to ${snapshot.property.max_guests}`,
  };
}

function makeSignatureFeatures(snapshot: OwnerRezSnapshot): SignatureFeature[] {
  const description = snapshot.listing.description ?? '';
  const level1 = sectionFromDescription(description, 'Level 1');
  const level2 = sectionFromDescription(description, 'Level 2');
  const level3 = sectionFromDescription(description, 'Level 3');
  const level5 = sectionFromDescription(description, 'Level 5');
  const location = sectionFromDescription(description, 'Location');

  return [
    {
      title: 'Private Elevator',
      body:
        firstSentences(level1, 3) ||
        'The private elevator connects all five levels, making arrivals, luggage, groceries, and everyday movement through the home feel easier.',
      image: elevatorImage,
      imageAlt: 'Private elevator and interior arrival at The Belvedere',
      tag: 'All 5 levels',
    },
    {
      title: 'Chef-Ready Kitchen',
      body:
        firstSentences(level2, 4) ||
        'The Level 2 kitchen is designed as the main gathering place, with a large island and a full suite of appliances, cookware, and dining essentials.',
      image: sourcePhoto(snapshot, 41),
      imageAlt: 'Kitchen and gathering area at The Belvedere',
      tag: 'Level 2',
    },
    {
      title: 'Private Pool',
      body:
        'The Level 2 outdoor living area centers around a private pool with programmable lights, fountains, and jets. The pool can be heated for an additional fee, and the outdoor grill keeps evenings easy.',
      image: sourcePhoto(snapshot, 42),
      imageAlt: 'Private pool at The Belvedere',
      tag: 'Outdoor living',
    },
    {
      title: 'Rooftop Living Area',
      body:
        firstSentences(level5, 3) ||
        'The Level 5 rooftop living area gives guests wide-open views over Rosemary Beach and the Gulf, with room to gather under the sky.',
      image: sourcePhoto(snapshot, 7),
      imageAlt: 'Rooftop living area at The Belvedere',
      tag: 'Gulf views',
    },
    {
      title: 'Rosemary Beach Location',
      body:
        firstSentences(location, 3) ||
        'Step outside and Rosemary Beach dining, shopping, and 30A paths are close at hand.',
      image: sourcePhoto(snapshot, 48),
      imageAlt: 'Rosemary Beach location near The Belvedere',
      tag: 'Heart of Rosemary',
    },
    {
      title: 'Primary Suite',
      body:
        firstSentences(level3, 3) ||
        'The primary suite spans the east side of Level 3 with a sitting room, private balcony, and two private bathrooms.',
      image: sourcePhoto(snapshot, 16),
      imageAlt: 'Primary suite at The Belvedere',
      tag: 'Level 3',
    },
  ].filter((feature) => feature.image);
}

function CuratedBanner({ generatedAt }: { generatedAt: string }) {
  return (
    <section className="bg-foreground-950 text-background-50 border-y border-background-50/10 py-5">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-secondary-200">
            Curated presentation sample
          </p>
          <p className="mt-1 text-sm text-background-100/75 max-w-3xl">
            The same Belvedere details, reorganized into a warmer, easier-to-browse property page. Sample refreshed {new Date(generatedAt).toLocaleString()}.
          </p>
        </div>
        <a
          href="/belvedere-data-preview"
          className="inline-flex items-center justify-center rounded-lg border border-background-50/20 px-4 py-2 text-sm text-background-50 hover:bg-background-50/10 transition-colors"
        >
          View before version
        </a>
      </div>
    </section>
  );
}

function MediaFieldPreview({ fields }: { fields?: OwnerRezSnapshot['custom_fields'] }) {
  const matterportUrl = fields?.matterport_url;
  const videoUrl = fields?.youtube_or_vimeo_url;
  const rows = [
    {
      label: 'Matterport URL',
      value: matterportUrl,
      note: matterportUrl ? 'Automatically places the virtual tour section on this page.' : 'No virtual tour link is listed yet.',
    },
    {
      label: 'YouTube/Vimeo URL',
      value: videoUrl,
      note: videoUrl ? 'Automatically adds the Watch Video button in the hero.' : 'No property video link is listed yet.',
    },
  ];

  return (
    <section className="border-b border-background-200/70 bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
          <div>
            <p className="mb-3 text-[11px] uppercase tracking-[0.24em] text-accent-700">
              Media fields
            </p>
            <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
              Enter it once,
              <span className="block italic font-light text-foreground-600">place it beautifully.</span>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600">
              This test page is reading dedicated property media fields, then using those links
              to place the tour and video controls in the right parts of the page.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {rows.map((row) => (
              <div key={row.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-accent-700">{row.label}</p>
                <p className="mt-3 break-all rounded-lg bg-background-100 px-3 py-3 text-xs leading-relaxed text-foreground-700">
                  {row.value || 'Not listed'}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-500">{row.note}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function OwnerRezBelvedereCuratedPage() {
  const [snapshot, setSnapshot] = useState<OwnerRezSnapshot | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const ownerRezWidgets = ownerRezWidgetsForProperty('belvedere');
  const reviewsWidget = ownerRezWidgets.find((widget) => widget.kind === 'reviews');

  useEffect(() => {
    fetch(snapshotUrl)
      .then((response) => response.json())
      .then(setSnapshot)
      .catch(() => setSnapshot(null));
  }, []);

  const pageData = useMemo(() => {
    if (!snapshot) return null;
    const gallery = makeGallery(snapshot);
    return {
      gallery,
      hero: makeHero(snapshot, gallery),
      story: makeStory(snapshot),
      features: makeSignatureFeatures(snapshot),
      groups: makeEssentialGroups(snapshot),
    };
  }, [snapshot]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (!snapshot || !pageData) {
    return (
      <main className="min-h-screen bg-background-50 text-foreground-900 font-body">
        <Header />
        <section className="pt-40 pb-24 px-5 md:px-10 max-w-[900px] mx-auto">
          <p className="text-[11px] uppercase tracking-[0.22em] text-accent-700 mb-4">Curated property preview</p>
          <h1 className="font-heading text-5xl text-foreground-950">Loading the Belvedere transformation...</h1>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-background-50 text-foreground-900 font-body">
      <Header />
      <PropertyHero data={pageData.hero} onViewPhotos={() => openLightbox(0)} />
      <CuratedBanner generatedAt={snapshot.generated_at} />
      <MediaFieldPreview fields={snapshot.custom_fields} />
      <GalleryGrid images={pageData.gallery} propertyName={pageData.hero.name} onRequestOpen={openLightbox} />
      <PropertyMatterport propertyName={pageData.hero.name} src={pageData.hero.matterportUrl} />
      <EssentialInfo
        groups={pageData.groups}
        heading={{ lead: 'Property details,', accent: 'made guest-friendly.' }}
        footnote="This sample shows how property details can be shaped into a clearer guest experience through grouping, language, emphasis, and a final human review before publishing."
      />
      <PropertyStory data={pageData.story} propertyName={pageData.hero.name} />
      <SignatureFeatures features={pageData.features} />
      <OwnerRezWidgets propertyName={pageData.hero.name} widgets={ownerRezWidgets} />
      <PropertyReviews reviews={reviews} propertyName={pageData.hero.name} ownerRezWidget={reviewsWidget} />
      <PropertyConcierge config={{ ...conciergeConfig, propertyName: pageData.hero.name }} />
      <Footer />
      <StickyBookBar
        data={{
          propertyId: 'belvedere',
          propertyName: pageData.hero.name,
          location: `${snapshot.property.city} - 30A`,
          bedrooms: `${snapshot.property.bedrooms} bed`,
          sleeps: `Sleeps ${snapshot.property.max_guests}`,
        }}
      />
      <GalleryLightbox
        images={pageData.gallery}
        isOpen={lightboxOpen}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </main>
  );
}
